import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Modal from 'react-modal';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import { FiEdit, FiTrash } from 'react-icons/fi';
import ConditionalButton from '../components/common/ConditionalButton';
import ConditionalActions from '../components/common/ConditionalActions';

const emptyMatiere = { branche_id: '', lib_matiere: '', abbrev_matiere: '' };

const MatieresPage = () => {
  const [matieres, setMatieres] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyMatiere);

  useEffect(() => {
    fetchMatieres();
    fetchBranches();
  }, []);

  const fetchMatieres = async () => {
    const res = await api.get('/matieres');
    setMatieres(res.data.data || []);
  };

  const fetchBranches = async () => {
    const res = await api.get('/branches');
    setBranches(res.data.data || []);
  };

  const handleAdd = () => {
    setEditing(null);
    setForm(emptyMatiere);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditing(item);
    setForm({ branche_id: item.branche_id, lib_matiere: item.lib_matiere, abbrev_matiere: item.abbrev_matiere });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Supprimer cette matière ?',
      text: 'Cette action est irréversible.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
    });
    if (result.isConfirmed) {
      await api.delete(`/matieres/${id}`);
      fetchMatieres();
      Swal.fire('Supprimé !', 'La matière a été supprimée.', 'success');
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditing(null);
    setForm(emptyMatiere);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.lib_matiere) {
      await Swal.fire({ icon: 'error', title: 'Erreur', text: 'Le nom de la matière est obligatoire.' });
      return;
    }
    if (!form.branche_id) {
      await Swal.fire({ icon: 'error', title: 'Erreur', text: 'La branche est obligatoire.' });
      return;
    }
    if (editing) {
      await api.put(`/matieres/${editing.id}`, form);
    } else {
      await api.post('/matieres', form);
    }
    fetchMatieres();
    setShowModal(false);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Matières</h1>
        <ConditionalButton
          entity="matieres"
          permission="create"
          className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
          onClick={handleAdd}
        >
          Nouvelle matière
        </ConditionalButton>
      </div>
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Branche</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Nom</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Abréviation</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {matieres.length === 0 ? (
              <tr>
                <td colSpan="4" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">Aucune matière</td>
              </tr>
            ) : (
              matieres.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/80 transition-colors duration-150">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{branches.find(b => b.id === item.branche_id)?.lib_branche || item.branche_id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-200">{item.lib_matiere}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{item.abbrev_matiere}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                    <ConditionalActions
                      entity="matieres"
                      onEdit={() => handleEdit(item)}
                      onDelete={() => handleDelete(item.id)}
                      editIcon={FiEdit}
                      deleteIcon={FiTrash}
                      className="flex space-x-2 justify-end"
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Modal
        isOpen={showModal}
        onRequestClose={handleModalClose}
        contentLabel="Matière"
        ariaHideApp={false}
        style={{
          overlay: {
            backgroundColor: 'rgba(0,0,0,0.75)',
            zIndex: 1000
          },
          content: {
            maxWidth: '400px',
            width: '100%',
            margin: 'auto',
            height: '380px',
            borderRadius: 12,
            overflow: 'hidden',
            padding: 0,
            backgroundColor: document.documentElement.classList.contains('dark') ? '#1F2937' : '#fff',
            color: document.documentElement.classList.contains('dark') ? '#E5E7EB' : '#111827'
          }
        }}
      >
        <div className="flex justify-between items-center px-6 pt-4 pb-2 border-b dark:border-gray-700">
          <h2 className="text-lg font-semibold dark:text-gray-100">{editing ? 'Modifier' : 'Nouvelle'} matière</h2>
          <button onClick={handleModalClose} className="text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 text-2xl font-bold">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 dark:bg-gray-800 flex flex-col h-full">
          <div className="flex-1 space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1 dark:text-gray-200">Branche *</label>
              <select
                name="branche_id"
                value={form.branche_id}
                onChange={handleChange}
                className="w-full border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              >
                <option value="">Sélectionner...</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.lib_branche}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 dark:text-gray-200">Libellé *</label>
              <input
                type="text"
                name="lib_matiere"
                value={form.lib_matiere}
                onChange={handleChange}
                className="w-full border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 dark:text-gray-200">Abréviation *</label>
              <input
                type="text"
                name="abbrev_matiere"
                value={form.abbrev_matiere}
                onChange={handleChange}
                className="w-full border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
                maxLength={10}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6 pt-4 border-t dark:border-gray-700">
            <button type="button" onClick={handleModalClose} className="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600">Annuler</button>
            <ConditionalButton
              entity="matieres"
              permission={editing ? "update" : "create"}
              type="submit"
              className="px-4 py-2 rounded bg-primary-600 text-white hover:bg-primary-700"
            >
              {editing ? 'Enregistrer' : 'Créer'}
            </ConditionalButton>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default MatieresPage;
